import type { ICitationResolution } from "@/api/chat/chat.types";

/** Context needed to anchor a citation deep-link. Without both fields a
 * `cite://` marker can't be made openable, so it degrades to plain text. */
export interface CitationLinkContext {
    /** The `/organizations/chat/:chatId` route param. */
    chatId?: string;
    /** Origin for the deep-link (e.g. `window.location.origin`). */
    appHost?: string;
}

// Matches an inline citation link `[label](cite://handle)`. The server stamps
// these into assistant text; `handle` is opaque (e.g. `c1`) and only resolves
// inside the live chat, so any text export must rewrite or drop it.
const CITE_LINK_RE = /\[([^\]]*)\]\(cite:\/\/([^)\s]+)\)/g;

/** Build the chat deep-link a citation points at, mirroring the backend xlsx
 * generator so every export surface (markdown download, copy, single-message
 * export, xlsx) produces the same URL shape. */
export function citationDeepLink(
    appHost: string,
    chatId: string,
    documentId: number,
    page: number | null
): string {
    const base = `${appHost}/organizations/chat/${chatId}?open_document=${documentId}`;
    return page != null ? `${base}&page=${page}` : base;
}

/** Rewrite every `cite://` link in a markdown string. A resolvable handle
 * (with a chat to anchor it) becomes a real deep-link; anything else drops to
 * the bare label so the export never carries a link that can't open. */
export function resolveCitationLinks(
    text: string,
    resolutions: ICitationResolution[] | undefined,
    ctx: CitationLinkContext
): string {
    const byHandle = new Map((resolutions ?? []).map((r) => [r.handle, r]));
    return text.replace(
        CITE_LINK_RE,
        (_match, label: string, handle: string) => {
            const resolution = byHandle.get(handle);
            if (!resolution || !ctx.chatId || !ctx.appHost) return label;
            return `[${label}](${citationDeepLink(
                ctx.appHost,
                ctx.chatId,
                resolution.document_id,
                resolution.page
            )})`;
        }
    );
}
